import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  InputGroup,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { LOCALITY_OVERVIEW_API } from "constants/endpoints";
import useApi from "hooks/useApi";
import Pagination from "components/pagination/Pagination";
import ErrorDisplay from "components/errorDisplay/ErrorDisplay";
import Menu from "components/menu/MainMenu";
import { OverlayLoaderComponent } from "components/loader/Loader";
import SidebarFilter from "components/sidebarFilter/SidebarFilter";
import { ENTITY_FILTER_TYPES, ENTITY_COLUMNS_TYPES } from "utils/common";
import {
  getFilterFromLocalStorage,
  setFilterToLocalStorage,
} from "utils/common";
import ManageColumns from "components/manageColumns/ManageColumns";
import NoDataFound from "components/noDataFound/NoDataFound";
import CustomTable from "components/table/CustomTable";
import { localityOverviewColumns } from "./variables";
import { formatLocalityOverviewData } from "utils/common";
import { showToast } from "components/toast/Toast";
import { SearchBar } from "components/navbar/searchBar/SearchBar";
import { useLocation } from "react-router-dom";
import { LOCALITY_LIST_STATUS } from "constants/filterDropdownData";
import { USER_STATUS } from "constants/filterDropdownData";
import { areAllStatusesSame } from "utils/common";
import MultiSelectMenu from "components/menu/MultiSelectMenu";
import ConfirmationModal from "components/modal/ConfirmationModal";
import { PRICE_TRENDS_API } from "constants/endpoints";

const PAGE_SIZE = 50;
const today = new Date();
const tenDaysAgo = new Date();
tenDaysAgo.setDate(today.getDate() - 10);

const excludedColumnVisiblity = ["slug_url", "username"];

export default function LocalityOverview() {
  const { apiCall, loading } = useApi();
  const [userData, setUserData] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [apiError, setApiError] = useState(null);
  const lsFiltersUsersPageSize = getFilterFromLocalStorage(
    ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE
  );
  const [pageSize, setPageSize] = useState(
    lsFiltersUsersPageSize && Number.isInteger(lsFiltersUsersPageSize)
      ? lsFiltersUsersPageSize
      : PAGE_SIZE
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { search } = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(search));
  const [manageColumnSelectAllCheckbox, setManageColumnSelectAllCheckbox] =
    useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedRowsData, setSelectedRowsData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchData = useCallback(
    async (text) => {
      const validPageSize = Number.isInteger(pageSize) ? pageSize : PAGE_SIZE;
      try {
        const response = await apiCall.get(`${LOCALITY_OVERVIEW_API}`, {
          page: currentPage,
          limit: validPageSize,
          search_text: text, // Add the search text parameter here
        });

        const rawData = response?.data?.overviews || [];
        const formattedData = formatLocalityOverviewData(rawData);
        setTotalPages(Math.ceil(response?.data?.count / pageSize));
        setTotalCount(response?.data?.count);
        setUserData(formattedData);
      } catch (error) {
        console.error("Error fetching locality overview:", error);
        setApiError(error);
      }
    },
    [currentPage, pageSize, searchText]
  );

  const handleRowSelect = useCallback(
    (row) => {
      const findData = selectedRowsData.find(
        (item) => item.location_slug === row.location_slug
      );
      setSelectedRowsData((prev) => {
        if (findData) {
          return prev.filter(
            (item) => item.location_slug !== findData.location_slug
          );
        } else {
          return [...prev, row];
        }
      });
    },
    [setSelectedRowsData, selectedRowsData]
  );
  const handleUserAction = async ({
    updatedPropertyType,
    id: selectedId,
    status,
  }) => {
    try {
      console.log("called handleUserAction with:", status, selectedId);

      if (status === "Delete Price Trend") {
        setIsModalOpen(true);
        return;
      }
      // Find the locality overview data by ID
      const data = userData.find((item) => item?._id === selectedId);

      if (!data) {
        console.error("Locality overview data not found for ID:", selectedId);
        return;
      }

      const { _id } = data;
      await apiCall.patch(LOCALITY_OVERVIEW_API, {
        ids: [_id],
        is_editable: updatedPropertyType,
      });
      // Update the local state
      setUserData((prevLocalityData) =>
        prevLocalityData.map((item) =>
          item._id === _id
            ? { ...item, is_editable: updatedPropertyType }
            : item
        )
      );
      // Show success toast
      showToast({
        message: "Editable updated successfully",
        success: true,
        customStyle: {
          background: "#52c41a",
          color: "#fff",
          padding: "10px",
        },
      });
    } catch (error) {
      console.error("Error handling user action:", error);
      const errorMessage =
        error?.response?.data?.error?.message || error?.response?.data?.error;
      showToast({
        message: `Error: ${errorMessage}`,
        success: false,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });

      if (error.response.status !== 409 && error.response.status !== 400) {
        onClose();
      }
    }
  };

  useEffect(() => {
    if (queryObject?.search_text) {
      setSearchText(queryObject?.search_text);
      handleFetchSearch(queryObject?.search_text);
    } else {
      setSearchText("");
      fetchData();
    }
  }, [currentPage, pageSize, queryObject?.search_text]);

  const columns = useMemo(
    () => localityOverviewColumns,
    [localityOverviewColumns]
  );
  const data = useMemo(() => {
    return formatLocalityOverviewData(userData).map((item) => ({
      ...item, // Ensure id is included
    }));
  }, [userData]);

  const selectedRowsSlug = selectedRowsData.map((item) => item.location_slug);
  const selectedData = useMemo(
    () =>
      selectedRowsSlug.map((slug) =>
        data.find((row) => row.location_slug === slug)
      ),
    [selectedRowsSlug, data]
  );

  const handleMultipleUpdateStatus = async ({ updatedStatus }) => {
    const idsToUpdate = selectedRowsData.map((item) => item._id);
    if (idsToUpdate.length === 0) {
      showToast({
        message: "No rows selected for update",
        customStyle: {
          background: "#ff4d4f",
          color: "#fff",
          padding: "10px",
        },
      });
      return;
    }

    try {
      await apiCall.patch(LOCALITY_OVERVIEW_API, {
        ids: idsToUpdate,
        is_editable: updatedStatus,
      });

      setUserData((prevLocalityData) =>
        prevLocalityData.map((item) =>
          idsToUpdate.includes(item._id)
            ? { ...item, is_editable: updatedStatus }
            : item
        )
      );

      showToast({
        message: "Editable status updated successfully",
        customStyle: {
          background: "green",
          color: "#fff",
          padding: "10px",
        },
      });
      setSelectedRowsData([]);
    } catch (error) {
      console.error("Error updating editable status:", error);
      const errorMessage =
        error?.response?.data?.error?.message || error?.response?.data?.error;
      showToast({
        message: `Error: ${errorMessage}`,
        customStyle: {
          background: "#F24052",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  };

  const lsLocalStorageManageColumns = getFilterFromLocalStorage(
    ENTITY_COLUMNS_TYPES.LOCALITY_OVERVIEW_COLUMNS
  );

  const [columnVisibility, setColumnVisibility] = useState(
    lsLocalStorageManageColumns
      ? lsLocalStorageManageColumns
      : columns.reduce((acc, column) => {
          acc[column.accessor] = true;
          return acc;
        }, {})
  );

  const handleColumnChange = (accessor) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [accessor]: !prev[accessor],
    }));
  };

  const handleFetchSearch = useCallback(
    (text) => {
      setCurrentPage(1);
      fetchData(text);
    },
    [fetchData]
  );

  const handleInputFormSubmit = (e) => {
    e.preventDefault();
    handleFetchSearch(searchText);
  };

  useEffect(() => {
    setFilterToLocalStorage(
      ENTITY_COLUMNS_TYPES.LOCALITY_OVERVIEW_COLUMNS,
      columnVisibility
    );
  }, [columnVisibility]);

  const visibleColumns = columns.filter(
    (column) =>
      columnVisibility[column.accessor] &&
      !excludedColumnVisiblity.includes(column.accessor)
  );

  const handlePriceTrendDelete = useCallback(async () => {
    const { location_slug } = selectedAction || {};
    try {
      if (!location_slug) return;
      await apiCall.delete(`${PRICE_TRENDS_API}/delete`, { location_slug });
      showToast({
        message: "Price Trend Deleted Successfully",
        customStyle: {
          background: "green",
          color: "#fff",
          padding: "10px",
        },
      });
    } catch (error) {
      showToast({
        message:
          error.response?.data?.error?.message ||
          error?.message ||
          "Something went wrong",
        success: false,
        customStyle: {
          background: "#ff0000",
          color: "#fff",
          padding: "10px",
        },
      });
    }
  }, [selectedAction]);

  if (apiError) {
    return <ErrorDisplay error={apiError} />;
  }
  return (
    <>
      <Box height="100vh" bg="#F8FAFB" mr={14} pr={6}>
        <Flex
          justify="space-between"
          align="center"
          p="2"
          boxShadow="sm"
          borderRadius="sm"
          pos="fixed"
          style={{ width: "calc(100vw - 96px)" }}
          zIndex={1000}
        >
          <HStack spacing="4" p={2}>
            <ManageColumns
              columns={columns}
              handleColumnChange={handleColumnChange}
              columnVisibility={columnVisibility}
              setManageColumnSelectAllCheckbox={
                setManageColumnSelectAllCheckbox
              }
              manageColumnSelectḀ̥̥̥̥̥̥̥̥llCheckbox={manageColumnSelectAllCheckbox}
            />
            <form onSubmit={handleInputFormSubmit}>
              <InputGroup size="sm">
                <SearchBar
                  background="#F9F9FB"
                  color="#637381"
                  setSearchText={(e) => setSearchText(e.target.value)}
                  searchText={searchText}
                  borderRadius="5px"
                  borderWidth="0.5px"
                  borderColor="#637381"
                />
              </InputGroup>
            </form>
          </HStack>
          <Text
            borderRadius="5px"
            borderWidth="0.5px"
            borderColor="#637381"
            background="#F9F9FB"
            color="#637381"
            p={1}
          >
            Total Locality Overviews: {totalCount}
          </Text>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(value) => setCurrentPage(value)}
            totalCount={totalCount}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageSizeToLocalStorage={(value) =>
              setFilterToLocalStorage(
                ENTITY_FILTER_TYPES.REPORT_PAGE_SIZE,
                value
              )
            }
          />
        </Flex>
        <SidebarFilter />
        {userData?.length !== 0 ? (
          <>
            <CustomTable
              columns={visibleColumns}
              data={data}
              onOpen={onOpen}
              onClose={onClose}
              isOpen={isOpen}
              onSelectedAction={setSelectedAction}
              selectedRowsData={selectedRowsData}
              selectedData={selectedData}
              setSelectedRowsData={setSelectedRowsData}
              selectedRowsSlug={selectedRowsSlug}
              handleRowSelect={handleRowSelect}
              menuComponent={
                <Menu
                  data={Object.values(USER_STATUS)}
                  onUpdate={() => {}}
                  onUpdatePropertyStatus={handleUserAction}
                  selectedAction={selectedAction}
                  dataType={"Locality"}
                />
              }
              multiSelectMenuComponent={
                selectedRowsSlug?.length > 0 &&
                areAllStatusesSame(
                  selectedData.map((item) => item?.is_editable)
                ) && (
                  <MultiSelectMenu
                    multiSelectCurrentValue={selectedData[0]?.is_editable}
                    onMultipleUpdateStatus={handleMultipleUpdateStatus}
                    selectedAction={selectedAction}
                    selectedRowsData={selectedRowsData}
                    bgColor="#B2F5EA"
                    dataType="Locality"
                  />
                )
              }
            />
          </>
        ) : !loading ? (
          <NoDataFound />
        ) : null}
        <OverlayLoaderComponent isOpen={loading} />
        {isModalOpen && (
          <ConfirmationModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Confirm Deletion"
            message="Are you sure you want to delete the price trend for the selected locality? This action cannot be undone."
            isLoading={loading}
            isDangerous
            confirmText="Delete"
            onConfirm={handlePriceTrendDelete}
            variant="danger"
          />
        )}
      </Box>
    </>
  );
}
